import { Package, DollarSign, Calendar, Link as LinkIcon, Tag, TrendingUp } from 'lucide-react';
import Badge from '@/components/common/Badge';

const PLUGIN_ICONS = {
  price_analyzer: DollarSign,
  tracking_number: Package,
  calendar_parser: Calendar
};

const PLUGIN_LABELS = {
  price_analyzer: 'Price Analysis',
  tracking_number: 'Package Tracking',
  calendar_parser: 'Calendar Events'
};

export default function PluginDataDisplay({ pluginData }) {
  if (!pluginData || pluginData.length === 0) {
    return null;
  }

  // Group by plugin
  const grouped = pluginData.reduce((acc, item) => {
    if (!acc[item.plugin_name]) {
      acc[item.plugin_name] = [];
    }
    acc[item.plugin_name].push(item);
    return {};
  }, {});

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([pluginName, items]) => {
        const Icon = PLUGIN_ICONS[pluginName] || Tag;
        const label = PLUGIN_LABELS[pluginName] || pluginName;

        return (
          <div key={pluginName} className="p-4 rounded-lg border bg-background">
            <div className="flex items-center gap-2 mb-3">
              <Icon className="h-4 w-4 text-muted-foreground" />
              <h4 className="font-medium text-sm">{label}</h4>
              <Badge variant="outline" className="text-xs ml-auto">
                {items.length} {items.length === 1 ? 'item' : 'items'}
              </Badge>
            </div>

            <div className="space-y-2">
              {items.map(item => (
                <PluginDataItem
                  key={item.id}
                  pluginName={pluginName}
                  dataKey={item.data_key}
                  dataValue={item.data_value}
                  confidence={item.confidence}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PluginDataItem({ pluginName, dataKey, dataValue, confidence }) {
  // Format display based on plugin and key
  const formatted = formatPluginData(pluginName, dataKey, dataValue);

  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <div className="flex-1 min-w-0">
        <p className="text-muted-foreground text-xs mb-1">
          {formatDataKey(dataKey)}
        </p>
        <p className="font-medium truncate">{formatted.display}</p>
        {formatted.link && (
          <a
            href={formatted.link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1"
          >
            <LinkIcon className="h-3 w-3" />
            {formatted.linkLabel || 'View'}
          </a>
        )}
      </div>
      {confidence && (
        <Badge variant="outline" className="text-xs flex-shrink-0">
          {Math.round(confidence * 100)}%
        </Badge>
      )}
    </div>
  );
}

function formatDataKey(key) {
  // Convert snake_case to Title Case
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatPluginData(pluginName, dataKey, dataValue) {
  // Try to parse as JSON first
  let parsed = dataValue;
  try {
    parsed = JSON.parse(dataValue);
  } catch (e) {
    // Not JSON, use as string
  }

  // Plugin-specific formatting
  if (pluginName === 'price_analyzer') {
    if (dataKey === 'main_price' || dataKey === 'main_price_amount') {
      return { display: dataValue };
    }
    if (dataKey === 'all_prices' && Array.isArray(parsed)) {
      return {
        display: parsed.map(p => p.formatted || p.raw).join(', ')
      };
    }
    if (dataKey === 'context') {
      return { display: dataValue };
    }
  }

  if (pluginName === 'tracking_number') {
    if (dataKey === 'tracking_number') {
      return {
        display: dataValue,
        link: null // Link will come from tracking_url
      };
    }
    if (dataKey === 'tracking_url') {
      return {
        display: 'Track Package',
        link: dataValue,
        linkLabel: 'Track Package'
      };
    }
    if (dataKey === 'carrier') {
      return { display: dataValue };
    }
  }

  if (pluginName === 'calendar_parser') {
    if (dataKey === 'first_date') {
      const date = new Date(dataValue);
      return {
        display: date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        })
      };
    }
  }

  // Default formatting
  if (typeof parsed === 'object') {
    return { display: JSON.stringify(parsed, null, 2) };
  }

  return { display: String(dataValue) };
}
